import {
   Checkbox,
   Flex,
   HStack,
   Input,
   InputGroup,
   InputLeftElement,
   InputRightElement,
   List,
   ListItem,
   Popover,
   PopoverBody,
   PopoverContent,
   PopoverTrigger,
   Tag,
   TagCloseButton,
   Text,
} from '@chakra-ui/react';
import { faChevronDown, faMagnifyingGlass, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { useRef, useState } from 'react';
import { useDebounced } from '@dozuki/web-js/components';
import { useLocalization } from '~/framework/localization';
import { MINIMUM_SEARCH_TERM_LENGTH, useTeamsSearch } from '~/pages/Courses/_shared/useTeamsSearch';
import { Icon, InfiniteScrollForIncrementalQuery } from '~/ui-components';
import { useTeams } from '..';

export type MultiTeamSelectProps = {
   readonly value: number[];
   readonly onChange: (value: number[]) => void;
};

export function MultiTeamSelect({ value: teamids, onChange }: MultiTeamSelectProps) {
   const { _js } = useLocalization();

   const searchInputRef = useRef<null | HTMLInputElement>(null);
   const [searchTerm, setSearchTerm] = useState('');
   const debouncedSearchTerm = useDebounced(searchTerm, 500);

   const teamsSearchQuery = useTeamsSearch(debouncedSearchTerm, 5);
   const { flattenedData: teams } = teamsSearchQuery;

   const { data: selectedTeams } = useTeams(teamids, {
      keepPreviousData: true,
   });

   function toggleSelected(selectedTeamId: number) {
      if (teamids.includes(selectedTeamId)) {
         onChange(teamids.filter(teamid => teamid !== selectedTeamId));
      } else {
         onChange([...teamids, selectedTeamId]);
      }
   }

   function deselectTeam(selectedTeamId: number) {
      onChange(teamids.filter(teamid => teamid !== selectedTeamId));
   }

   function deselectAll() {
      onChange([]);
   }

   return (
      <Popover initialFocusRef={searchInputRef} matchWidth>
         <PopoverTrigger>
            <InputGroup>
               <Input
                  as="button"
                  aria-label={_js('Select teams')}
                  display="flex"
                  cursor="pointer"
                  height="initial"
                  minHeight={8}
                  p={2}
               >
                  {!selectedTeams?.length && (
                     <Text ml={2} variant="muted">
                        {_js('Select teams')}
                     </Text>
                  )}
                  <HStack aria-label={_js('Selected teams')} flex={1} flexWrap="wrap">
                     {selectedTeams?.map(selectedTeam => (
                        <Tag key={selectedTeam.teamid} bgColor="blue.200" fontWeight="bold">
                           {selectedTeam.name}
                           <TagCloseButton
                              aria-label={_js('Deselect %1', selectedTeam.name)}
                              onClick={() => deselectTeam(selectedTeam.teamid)}
                           />
                        </Tag>
                     ))}
                  </HStack>
                  <InputRightElement h="100%" w="initial" right={3} position="initial">
                     {teamids.length ? (
                        <Icon
                           role="button"
                           aria-label={_js('Deselect all teams')}
                           type={faTimes}
                           mr={3}
                           onClick={deselectAll}
                        />
                     ) : null}
                     <Icon type={faChevronDown} />
                  </InputRightElement>
               </Input>
            </InputGroup>
         </PopoverTrigger>
         <PopoverContent w="100%">
            <PopoverBody w="100%">
               <InputGroup>
                  <InputLeftElement>
                     <Icon type={faMagnifyingGlass} />
                  </InputLeftElement>
                  <Input
                     ref={searchInputRef}
                     aria-label={_js('Search teams')}
                     placeholder={_js('Search')}
                     value={searchTerm}
                     onChange={e => setSearchTerm(e.target.value)}
                  />
               </InputGroup>
               <InfiniteScrollForIncrementalQuery
                  query={teamsSearchQuery}
                  contained
                  maxHeight={300}
               >
                  {teams?.length === 0 && (
                     <Flex
                        flex={1}
                        flexDirection="column"
                        align="center"
                        justify="center"
                        height={200}
                        mt={4}
                     >
                        <Text variant="subtitle">{_js('There are no teams to display')}</Text>
                        {debouncedSearchTerm.length >= MINIMUM_SEARCH_TERM_LENGTH && (
                           <Text variant="muted">
                              {_js('Try searching for something different.')}
                           </Text>
                        )}
                     </Flex>
                  )}
                  <List spacing={4} mt={4} ml={2}>
                     {teams?.map(team => (
                        <ListItem key={team.teamid}>
                           <Checkbox
                              verticalAlign="middle"
                              isChecked={teamids.includes(team.teamid)}
                              onChange={() => toggleSelected(team.teamid)}
                           >
                              {team.name}
                           </Checkbox>
                        </ListItem>
                     ))}
                  </List>
               </InfiniteScrollForIncrementalQuery>
            </PopoverBody>
         </PopoverContent>
      </Popover>
   );
}
